<template>
  <RebateBadge
    v-if="badge && badge.rebate"
    :default-price="defaultPrice"
    :style="cssBadgePosition"
    :was-price="wasPrice"
    :pre-text="badge.preText"
    :magenta="badge.magenta"
    :black="badge.black"
    :white="badge.white"
    class="promotion-badge"
  />
  <Badge
    v-else-if="badge"
    :default-size="130"
    :mobile-size="90"
    :style="cssBadgePosition"
    :magenta="badge.magenta"
    :black="badge.black"
    :white="badge.white"
    bold
    class="promotion-badge"
  >
    <span
      class="promotion-badge__text"
      v-html="badge.text"
    />

    <Modal
      v-if="modalTexts"
      :heading-text="modalHeading"
      close-on-backdrop
      class="promotion-badge__modal"
    >
      <template #trigger="{ open }">
        <LinkButton
          class="promotion-badge__modal-footnote btn--footnote"
          tag="button"
          type="button"
          no-style
          transparent
          aria-label="Fußnote"
          @click="open"
        >
          *
        </LinkButton>
      </template>
      <div
        v-for="(text, index) in modalTexts"
        :key="index"
        class="promotion-badge__modal-text"
      >
        <div v-html="text"></div>
      </div>
    </Modal>
    <slot />
  </Badge>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { Badge, LinkButton, Modal } from '@i22-td-smarthome/component-library';
import RebateBadge from '~/components/rebate-badge.vue';
import { isMobileScreen } from '~/composables/use-screen-size';
import { type PromotionBadgeType } from '~/lib/promotions-type';
import type { ListOffer } from '~/lib/goliath/offers';
import { getPrice } from '@i22/rocket';

const props = defineProps({
  badge: {
    type: Object as PropType<PromotionBadgeType | undefined>,
    default: undefined,
  },
  modalHeading: { type: String, default: undefined },
  modalTexts: {
    type: Array as PropType<string[]>,
    default: undefined,
  },
  offer: {
    type: Object as PropType<ListOffer | undefined>,
    default: undefined,
  },
});

const cssBadgePosition = computed(() => {
  const { badge } = props;
  if (!badge) return undefined;
  let styles = '';

  if (!isMobileScreen.value) {
    if (badge?.position?.default) {
      styles += `top: ${badge.position.default.top}; left: ${badge.position.default.left};`;
    }
  } else if (badge?.position?.mobile) {
    styles += `top: ${badge.position.mobile.top}; left: ${badge.position.mobile.left};`;
  }
  if (badge.style) {
    styles += badge.style;
  }

  return styles;
});

const defaultPrice = computed(() => {
  if (!props.offer?.prices) return undefined;
  const price = getPrice(props.offer.prices || [], 'DEFAULT', 'ONETIME');
  return price?.amount;
});

const wasPrice = computed(() => {
  if (!props.offer?.prices) return undefined;
  if (!props.badge?.rebateShowWasPrice) return undefined;
  const price = getPrice(props.offer.prices || [], 'WAS', 'ONETIME');
  return price?.amount;
});
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.promotion-badge {
  position: absolute;
  top: 10%;
  left: 10%;

  .promotion-badge__text {
    @include mq($mq-md) {
      font-size: 1.2em;
      line-height: 1.3em;
    }
  }
}
</style>
